import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { ReactComponent as ArrowLeft } from '../../img/arrow_left.svg';
import { ReactComponent as Ghost } from '../../img/ghost.svg';
import MqttComponent from './mqttComponent';
import { UnitMqttSniffer } from './UnitMqttSniffer';
import {InfoTable} from './infoTable';
import {NetworkTable} from './networkTable';
import { BooleanTable } from './booleanTable';
import { IssuesTableHeader } from './issuesTableHeader';
import { getIssueProperty, getIssueCombinedKey, formatIssueTimestamp, validateIssue } from '../../schemas/issueSchema';
import { getUnitProperty, formatUnitTimestamp, validateUnit, getDefaultScheduledWF, getDefaultPostAssemblyCalibration, getDefaultParameters } from '../../schemas/unitSchema';
import { validateKpodHistoric } from '../../schemas/kpodHistoricSchema';
import { SmartActionsSection } from './SmartActionsSection';
import { InstallationInfoColumn } from './InstallationInfoColumn';
import { UnitStatusSelect } from './UnitStatusSelect';
import { UnitStatusColumn } from './UnitStatusColumn';
import { IssueGroupingToggle } from './IssueGroupingToggle';
import { formatTimestamp } from '../../utils/formatters';
import { setDeepValue } from '../../utils/objectUtils';
import { ScheduledWaterFlushSection } from './ScheduledWaterflushSection';
import { PostAssemblyCalibrationSection } from './PostAssemblyCalibrationSection';
import { ParametersSection } from './ParametersSection';

const toMs = (installation) => {
  if (!installation) return 0;
  if (typeof installation === 'object' && installation.seconds) return installation.seconds * 1000;
  if (typeof installation === 'number') return installation;
  const d = new Date(installation);
  return isNaN(d) ? 0 : d.getTime();
};


export const UnitSection = ({
  classifiedIssuesbyUnitAndKey,
  issueIdFilter,
  handleSortIssue,
  sortFieldIssue,
  sortOrderIssue,
  handleHandleIssue,
  dashboardUnitsComplete,
  setNewIsSmartActionsEnabled,
  setNewScheduledWF,
  setNewPostAssemblyCalibration,
  setNewParameters,
  backToDashboard,
  kpodHistoricData,
  handleCloseAllIssues,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [groupIssues, setGroupIssues] = useState(true);
  const [isClosingAll, setIsClosingAll] = useState(false);
  
  const unitId = searchParams.get('unit');
  const selectedUnitItem = dashboardUnitsComplete[unitId];

  // Local state for editable fields - initialize from selectedUnitItem
  const [localIsSmartActionsEnabled, setLocalIsSmartActionsEnabled] = useState(false);
  const [localScheduledWF, setLocalScheduledWF] = useState(null);
  const [localPostAssemblyCalibration, setLocalPostAssemblyCalibration] = useState(null);

  // Initialize local state when unit changes
  useEffect(() => {
    if (selectedUnitItem) {
      setLocalIsSmartActionsEnabled(getUnitProperty(selectedUnitItem, 'isSmartActionsEnabled', false));
      const scheduledWF = getUnitProperty(selectedUnitItem, 'scheduledWF');
      setLocalScheduledWF(scheduledWF || getDefaultScheduledWF());
      const calibration = getUnitProperty(selectedUnitItem, 'postAssemblyCalibration');
      setLocalPostAssemblyCalibration(calibration || getDefaultPostAssemblyCalibration());
    }
    //Remove this warning...
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitId]);

  // Derive the flat list of issues for the current unit
  const currentIssuesList = useMemo(() => {
    if (!unitId || !classifiedIssuesbyUnitAndKey[unitId]) return [];

    // Flatten all issues from all grouped keys for this unit
    const allIssues = Object.values(classifiedIssuesbyUnitAndKey[unitId])
      .flat()
      .filter(item => {
        if (!validateIssue(item)) {
          console.warn('Invalid issue in currentIssuesList:', item);
          return false;
        }

        const issueId = getIssueProperty(item, 'Issue.ID');

        // Filter by issue ID
        if (issueIdFilter && issueId && !issueId.toString().toLowerCase().startsWith(issueIdFilter.toLowerCase())) {
          return false;
        }

        return true;
      });

    return allIssues;
  }, [unitId, classifiedIssuesbyUnitAndKey, issueIdFilter]);

  const displayedIssues = useMemo(() => {
    // Return empty array immediately when closing all issues
    if (isClosingAll) return [];

    if (!currentIssuesList || currentIssuesList.length === 0) return [];

    let issues = currentIssuesList;

    if (groupIssues && unitId) {
      const grouped = {};
      currentIssuesList.forEach(issue => {
        const combinedKey = getIssueCombinedKey(issue);
        if (!grouped[combinedKey]) {
          grouped[combinedKey] = issue;
        }
      });
      issues = Object.values(grouped);
    }

    // Apply sort — fall back to newest-first by creation timestamp
    const field = sortFieldIssue || 'createdAt';
    const order = sortFieldIssue ? sortOrderIssue : 'desc';
    issues = [...issues].sort((a, b) => {
      let aVal = getIssueProperty(a, field);
      let bVal = getIssueProperty(b, field);

      // Normalise timestamps to comparable numbers
      if (aVal && typeof aVal === 'object' && aVal.seconds) aVal = aVal.seconds;
      if (bVal && typeof bVal === 'object' && bVal.seconds) bVal = bVal.seconds;

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return order === 'asc' ? cmp : -cmp;
    });

    return issues;
  }, [currentIssuesList, groupIssues, unitId, isClosingAll, sortFieldIssue, sortOrderIssue]);

  const installationInfoProps = useMemo(() => {
    if (!selectedUnitItem) return null;

    const installationTimestamp = getUnitProperty(selectedUnitItem, 'time_installed');
    const lastInitTimestamp = getUnitProperty(selectedUnitItem, 'lastInitTimestamp');
    const rosterActive = getUnitProperty(selectedUnitItem, 'rosterActive', false);
    const lastLoginTimestamp = getUnitProperty(selectedUnitItem, 'lastLoginTimestamp');
    // Same value already shown as "LAST SCORE" on the main unit list — a score is
    // recorded on each use, so this doubles as the last-usage timestamp. Reusing it
    // avoids a second data fetch (and the RTDB permissions that fetch would need).
    const lastUsageTimestamp = getUnitProperty(selectedUnitItem, 'since_last_score');
    const waterReferenceTimestamp = getUnitProperty(selectedUnitItem, 'water_reference_ts');

    const timestampData = {
      installation: installationTimestamp ? formatUnitTimestamp(installationTimestamp) : '-',
      lastInit: lastInitTimestamp === '-' ? lastInitTimestamp : formatTimestamp(lastInitTimestamp),
      lastLogin: !rosterActive ? 'Disabled'
        : (lastLoginTimestamp === '-' ? lastLoginTimestamp : formatTimestamp(lastLoginTimestamp)),
      lastUsage: !lastUsageTimestamp || lastUsageTimestamp === '-' ? '-' : formatTimestamp(lastUsageTimestamp),
      // water_reference_ts arrives either as a Firestore {seconds, nanoseconds}
      // timestamp object or as a raw epoch-seconds string/number, depending on
      // which path wrote it — format each shape with the matching helper.
      waterReference: !waterReferenceTimestamp ? '-'
        : typeof waterReferenceTimestamp === 'object'
          ? formatUnitTimestamp(waterReferenceTimestamp)
          : formatTimestamp(waterReferenceTimestamp),
    };

    const kpodHistoric = kpodHistoricData[unitId]?.kpod_historic || {};

    const processedKpodData = Object.entries(kpodHistoric)
      .filter(([kpodId, data]) => {
        if (!validateKpodHistoric(data)) {
          console.warn(`Invalid kpod historic entry for ${kpodId}:`, data);
          return false;
        }
        return true;
      })
      .sort(([, a], [, b]) => {
        try {
          return toMs(b?.installation) - toMs(a?.installation);
        } catch {
          return 0;
        }
      });

    return { timestampData, processedKpodData };
  }, [selectedUnitItem, kpodHistoricData, unitId]);

  const networkInfoProps = useMemo(() => {
    if (!selectedUnitItem) return null;

    const statusInfo = getUnitProperty(selectedUnitItem, 'statusInfo');
    const ethernetIp = getUnitProperty(selectedUnitItem, 'statusInfo._ethernet_ip');
    const wifiIp = getUnitProperty(selectedUnitItem, 'statusInfo._wifi_ip');

    if (!statusInfo) return null;

    return { statusInfo, ethernetIp, wifiIp };
  }, [selectedUnitItem]);

  //This is for data chart
  const openSensorChart = useCallback(() => {
    // Get unit ID and center from the selectedUnitItem using the schema helpers
    const unitId = getUnitProperty(selectedUnitItem, 'unit', '');
    
    // Navigate to sensor chart with the correct query parameters
    navigate(`/sensor-chart?unit=${unitId}`);
  }, [navigate, selectedUnitItem]);

  useEffect(() => {
    if (isClosingAll && currentIssuesList.length === 0) {
      setIsClosingAll(false);
    }
  }, [currentIssuesList, isClosingAll]);

  // Early return AFTER all hooks have been called
  if (!selectedUnitItem || !validateUnit(selectedUnitItem)) {
    return (
      <div className="text-center mt-5">
        <h5>Unit not found. Please return to dashboard.</h5>
        <button className="btn btn-primary mt-3" onClick={backToDashboard}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  const getTotalIssueCount = () => {
    if (!groupIssues) {
      return currentIssuesList.length;
    }
    
    // When grouping, count groups
    return displayedIssues.length;
  };

  const getAllIssuesToClose = () => {
    if (!groupIssues) {
      // If not grouping, just return the current list
      return currentIssuesList;
    }
    
    // If grouping, we need to get ALL the underlying issues
    if (!unitId || !classifiedIssuesbyUnitAndKey[unitId]) {
      return currentIssuesList;
    }
    
    const allIssues = [];
    displayedIssues.forEach(displayedIssue => {
      const combinedKey = getIssueCombinedKey(displayedIssue);
      const groupedIssues = classifiedIssuesbyUnitAndKey[unitId][combinedKey];
      if (groupedIssues && groupedIssues.length > 0) {
        allIssues.push(...groupedIssues);
      } else {
        allIssues.push(displayedIssue);
      }
    });
    
    return allIssues;
  };

  function formatDate(dateString) {
    if (!dateString) return '';

    // Firebase timestamp object: { seconds, nanoseconds }
    if (typeof dateString === 'object' && dateString.seconds) {
      const d = new Date(dateString.seconds * 1000);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    }

    // Unix timestamp in milliseconds (number)
    if (typeof dateString === 'number') {
      const d = new Date(dateString);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    }

    // String yyyy-mm-dd
    const parts = String(dateString).split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day}/${month}/${year}`;
    }

    return String(dateString);
  }

  return (
    <div className="mt-4">
      <div className="text-start mb-3">
        <div className="d-flex align-items-center">
          <button
            type="button"
            className="btn btn-link ps-0 pe-2 mb-3"
            onClick={() => { backToDashboard() }}
          >
            <ArrowLeft style={{ width: '19px', height: '16px' }} />
          </button>
            <h5><strong>Unit {getUnitProperty(selectedUnitItem, 'unit', 'N/A')} Issues | {getUnitProperty(selectedUnitItem, 'center', 'N/A')} | {getUnitProperty(selectedUnitItem, 'description', 'No description')}</strong></h5>
        </div>
      </div>
    <div className="container">
      <div className="row pb-3">
        {/* First column*/}
        <UnitStatusColumn
          selectedUnitItem={selectedUnitItem}
          classifiedIssuesbyUnitAndKey={classifiedIssuesbyUnitAndKey}
          electedIsSmartActionsEnabled={localIsSmartActionsEnabled}
          setSelectedIsSmartActionsEnabled={setLocalIsSmartActionsEnabled}
          setNewIsSmartActionsEnabled={setNewIsSmartActionsEnabled}
          unitData={{
            description: getUnitProperty(selectedUnitItem, 'description', 'No description')
          }}
        />
        {/* Second column - Installation info and Historic Kpods */}
        {installationInfoProps && (
          <InstallationInfoColumn
            timestampData={installationInfoProps.timestampData}
            selectedUnitItem={selectedUnitItem}
            processedKpodData={installationInfoProps.processedKpodData}
            formatDate={formatDate}
          />
        )}
        {/* Third column - MQTT Component and Report Button */}
        <div className="col-12 col-lg-4">
          <SmartActionsSection
            selectedIsSmartActionsEnabled={localIsSmartActionsEnabled}
            handleSmartActionsChange={async () => {
              const newValue = !localIsSmartActionsEnabled;
              setLocalIsSmartActionsEnabled(newValue);
              try {
                await setNewIsSmartActionsEnabled(unitId, newValue); // Pass unitId
              } catch (error) {
                console.error('Error updating Smart Actions:', error);
              }
            }}
          />
          <UnitStatusSelect
            issue={{
              Installation: {
                Unit_ID: getUnitProperty(selectedUnitItem, 'unit', ''),
              }
            }}
          />
          <MqttComponent
            selectedUnitItem={selectedUnitItem}
          />
          {/* Updated button to open new window */}
          <Button
            variant="info"
            className="mqtt-button w-100"
            style={{ color: 'black'}}
            onClick={openSensorChart}
          >
            Sensor Chart
          </Button>
          <div className="mt-2">
            <UnitMqttSniffer unitId={unitId} />
          </div>
            </div>
        </div>
        {/* Scheduled Water Flush + Post-Assembly Calibration */}
        <div className="row g-3 pb-4">
          <div className="col-12">
            <ScheduledWaterFlushSection
              scheduledWF={localScheduledWF}
              onScheduledWFChange={async (fieldPath, value) => {
                setLocalScheduledWF(prev => setDeepValue(prev, fieldPath, value));
                try {
                  await setNewScheduledWF(unitId, fieldPath, value);
                } catch (error) {
                  console.error(`Error updating ${fieldPath}:`, error);
                }
              }}
            />
            <PostAssemblyCalibrationSection
              postAssemblyCalibration={localPostAssemblyCalibration}
              onCalibrationChange={async (fieldPath, value) => {
                setLocalPostAssemblyCalibration(prev => setDeepValue(prev, fieldPath, value));
                try {
                  await setNewPostAssemblyCalibration(unitId, fieldPath, value);
                } catch (error) {
                  console.error(`Error updating ${fieldPath}:`, error);
                }
              }}
            />
          </div>
        </div>
          {/* Network Tables — only relevant when the unit has network/status data */}
          {networkInfoProps && (
            <div className="row g-3 pt-4">
              <div className="col-12 col-md-4 mb-3">
                <NetworkTable title="Ethernet" data={networkInfoProps.ethernetIp} />
              </div>
              <div className="col-12 col-md-4 mb-3">
                <NetworkTable title="Wi-Fi" data={networkInfoProps.wifiIp} />
              </div>
              <div className="col-12 col-md-4 mb-3">
                <InfoTable
                  title="HW / SW"
                  statusInfo={networkInfoProps.statusInfo}
                  data={[
                    { label: 'Software version', key: '_software_version' },
                    { label: 'Hardware version', key: '_hardware_version' },
                    { label: 'Cartridge firmware', key: '_cartridge_firmware_version' },
                    { label: 'Cartridge hardware', key: '_cartridge_hardware_version' }
                  ]}
                />
              </div>
            </div>
          )}
          {/* Modes / Parameters — Parameters doesn't depend on network/status data, so it
              renders independently of networkInfoProps and always shows */}
          <div className="row g-3 pb-4">
            <div className="col-12 col-lg-6 mb-3">
              <BooleanTable
                title="MODES"
                statusInfo={getUnitProperty(selectedUnitItem, 'statusInfo')}
                data={[
                  { label: 'Forced flush readings', key: '_forced_waterflush_readings', colored: true, reverseColors: true },
                  { label: 'Periodic flush active', key: '_periodic_waterflush_active', colored: true },
                  { label: 'Liquid sensor trigger', key: '_liquid_sensor_active', colored: true },
                  { label: 'Flush button active', key: '_water_flush_button_active', colored: true },
                  { label: 'Out of order active', key: '_out_of_order_active', colored: true },
                  { label: 'Welcome screen visible', key: '_welcome_screen_texts_visible' },
                  { label: 'Score screen version', key: '_score_screen_version', type: 'text' },
                  { label: 'Wizard status', key: '_wizard_is_done', colored: true, reverseColors: true }
                ]}
              />
            </div>
            <div className="col-12 col-lg-6 mb-3">
              <ParametersSection
                parameters={getUnitProperty(selectedUnitItem, 'parameters') || getDefaultParameters()}
                calibrationEnabled={localPostAssemblyCalibration?.conductivity?.enable || false}
                calibrationScalarFactor={localPostAssemblyCalibration?.conductivity?.manualScalarFactor}
                onSave={async (newKPod) => {
                  await setNewParameters(unitId, 'kPod', newKPod);
                }}
              />
            </div>
          </div>
        </div>
        {/* Show only ERRORS header and Close All button on mobile */}
        <div className='row px-3 pb-2'>
          <div className="col-8">
            <div className="d-flex align-items-center">
              <div className="text-start me-3"><strong>ERRORS</strong></div>
              <IssueGroupingToggle
                groupIssues={groupIssues}
                onToggleGrouping={() => setGroupIssues(!groupIssues)}
              />
            </div>
          </div>
          <div className="col-4 text-end">
            {currentIssuesList.length > 0 && (
              <Button
                variant="danger"
                size="sm"
                onClick={async () => {
                  const totalIssues = getTotalIssueCount();
                  const allIssues = getAllIssuesToClose();
                  if (window.confirm(`Are you sure you want to close all ${totalIssues} issues?`)) {
                    setIsClosingAll(true); // Set flag before closing
                    await handleCloseAllIssues(allIssues);
                  }
                }}
              >
                Close All ({getTotalIssueCount()})
              </Button>
            )}
          </div>
        </div>
    {/* Issues table */}
      <div className='pb-4 px-3'>
        <div className="table-responsive">
          {displayedIssues.length > 0 ? (
          <table className="table table-striped table-hover bdr mb-5">
            <IssuesTableHeader handleSort={handleSortIssue} groupIssues={groupIssues} />
              <tbody>
                {displayedIssues.map((item, index) => {
                  const combinedKey = getIssueCombinedKey(item);
                  const issueCount = groupIssues ? (classifiedIssuesbyUnitAndKey[unitId]?.[combinedKey]?.length || 0) : 0;
                  
                  const issueID = getIssueProperty(item, 'Issue.ID') || 'N/A';
                  const issueDefinition = getIssueProperty(item, 'Issue.definition') || 'No description';
                  const deviceType = getIssueProperty(item, 'Installation.Dev_type') || 'Unknown';
                  
                  const timestamp = getIssueProperty(item, 'createdAt');
                  const formattedTimestamp = timestamp ? formatIssueTimestamp(timestamp) : null;
                  const timestampDisplay = formattedTimestamp ? formatTimestamp(formattedTimestamp) : '-';
                  const [dateDisplay, timeDisplay] = timestampDisplay !== '-' ? timestampDisplay.split(' ') : ['-', ''];
                  
                  return (
                    <tr
                      key={item.id || `issue-${index}-${issueID}`}
                          onClick={() => {
                            const issueGroup = classifiedIssuesbyUnitAndKey[unitId]?.[combinedKey] || [];
                            handleHandleIssue(item, issueGroup);
                          }}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        {groupIssues && issueCount > 1 ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            {issueID}
                            <span className='ms-2' style={{ fontSize: '0.9em', color: '#666' }}>
                              {`(x${issueCount})`}
                            </span>
                          </span>
                        ) : (
                          issueID
                        )}
                      </td>
                      <td>{timeDisplay ? `${dateDisplay} - ${timeDisplay}` : dateDisplay}</td>
                      <td style={{ textAlign: 'left' }}>{issueDefinition}</td>
                      <td>{deviceType}</td>
                    </tr>
                  );
                })}
              </tbody>
          </table>
          ) : (
          <div>
            <table className="table table-striped table-hover bdr mb-5">
              <IssuesTableHeader handleSort={handleSortIssue}  groupIssues={groupIssues} />
            </table>
            <div className="text-center mt-5">
              <Ghost style={{ width: '100%', maxWidth: '500px', height: 'auto' }}/>
            </div>
            <div className="text-center text-muted mt-4 mb-5">
              <h5>Oh, it looks like you have no issues!</h5>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};
